import type { FormEvent } from "react";
import { memo, useRef, useState } from "react";
import { Button, ButtonTypes, Input } from "@clintonelec/react-storybook";
import "Components/Auth/ForgotPasswordPage/ForgotPasswordPage.less";
import { NotificationType, notify } from "src/Notifications";
import { resetPassword } from "Data/API/Auth";
import type { ResetPasswordPayload } from "Interfaces";
import { useNavigate, useParams } from "react-router-dom";
import { escapeRegExp } from "lodash";
import { IconSize } from "Components/Icon";
import AuthPage from "Components/Auth/AuthPage";

type ResetPasswordFormFields = {
	password: HTMLInputElement;
	passwordConfirmation: HTMLInputElement;
} & HTMLFormElement;

const ForgotPasswordPage = () => {
	const [ password, setPassword ] = useState("");
	const [ submitted, setSubmitted ] = useState(false);
	const formRef = useRef<HTMLFormElement>(null);
	const params = useParams();
	const { token } = params;
	const navigate = useNavigate();

	const handlePasswordUpdate = (newPassword: string) => {
		setPassword(newPassword);
	};

	const handleSubmit = (event: FormEvent<ResetPasswordFormFields>) => {
		event.preventDefault();

		setSubmitted(true);

		const urlParams = new URLSearchParams(window.location.search);
		const email = urlParams.get("email");

		if (!email || !token) {
			return;
		}

		const payload: ResetPasswordPayload = {
			email,
			password: event?.currentTarget?.password?.value,
			passwordConfirmation: event?.currentTarget?.passwordConfirmation?.value,
			token
		};

		if (formRef.current?.checkValidity()) {
			resetPassword(payload)
				.then(() => {
					notify(NotificationType.SUCCESS, "Password Changed Successfully");

					navigate("/");
				})
				.catch((error) => {
					notify(NotificationType.ERROR, "Error Changing Password", error);
				});
		}
	};

	const forgotPasswordForm = (
		<div className="forgot-password-form">
			<div className="logo" />
			<form onSubmit={ handleSubmit } ref={ formRef } noValidate>
				<label>New Password</label>
				<Input
					name="password"
					password
					wrapClassName="verify-input-password"
					required
					onUpdate={ handlePasswordUpdate }
					noValidate={ !submitted }
					validityMessage={ "Password is required" }
				/>
				<label>Confirm New Password</label>
				<Input
					name="passwordConfirmation"
					password
					required
					validator={ `^${ escapeRegExp(password) }$` }
					noValidate={ !submitted }
					validityMessage={ "Password must match" }
				/>
				<Button
					id="submit-button"
					className="submit-button"
					type={ ButtonTypes.PRIMARY }
					htmlType="submit"
					icon={ {
						name: "lock-keyhole",
						size: IconSize.SMALLER
					} }
				>
					Reset Password
				</Button>
			</form>
		</div>
	);

	return <AuthPage form={ forgotPasswordForm } />;
};

export default memo(ForgotPasswordPage);
